// Multi-place selection for the new-route form: a places-only typeahead that
// appends to a list; each selected place becomes a route_place row on submit.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { PLACE_PICKER_SEARCH, type PlacePickerResult } from "../lib/queries";
import { PLACE_TYPE_ICONS, type PlaceType } from "../lib/format";
import { useT } from "../lib/i18n";
import { TextInput } from "./ui";

export type PickedPlace = { id: string; name: string; type: PlaceType; elevation: number | null };

export function PlacePicker({
  selected,
  onChange,
}: {
  selected: PickedPlace[];
  onChange: (places: PickedPlace[]) => void;
}) {
  const { t, labelizeT } = useT();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const suggestions = useQuery({
    queryKey: ["place-picker", debounced],
    enabled: debounced.length >= 2,
    queryFn: () => gqlRequest<PlacePickerResult>(PLACE_PICKER_SEARCH, { q: `%${debounced}%` }),
  });

  const selectedIds = new Set(selected.map((p) => p.id));
  const candidates = suggestions.data?.places.nodes.filter((p) => !selectedIds.has(p.id)) ?? [];

  function add(place: PickedPlace) {
    onChange([...selected, place]);
    setQuery("");
    setDebounced("");
  }

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <ul className="space-y-1.5">
          {selected.map((p) => {
            const Icon = PLACE_TYPE_ICONS[p.type] ?? MapPin;
            return (
              <li
                key={p.id}
                className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/5 px-3 py-2"
              >
                <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{p.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {labelizeT(p.type)}
                    {p.elevation ? ` · ${p.elevation} m` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((s) => s.id !== p.id))}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                  aria-label={t("picker.removePlaceAria", { name: p.name })}
                >
                  <X aria-hidden className="size-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="relative">
        <TextInput
          value={query}
          placeholder={selected.length ? t("picker.linkAnotherPlace") : t("picker.searchPlaces")}
          onChange={(e) => setQuery(e.target.value)}
        />
        {debounced.length >= 2 && suggestions.data && (
          <div className="absolute top-full z-10 mt-1.5 max-h-56 w-full divide-y divide-border overflow-y-auto rounded-xl border bg-popover text-popover-foreground shadow-panel">
            {candidates.length === 0 && <p className="p-3 text-sm text-muted-foreground">{t("picker.noPlaces")}</p>}
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => add(p)}
                className="block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="block truncate font-medium text-foreground">{p.name}</span>
                <span className="text-xs text-muted-foreground">
                  {labelizeT(p.type)}
                  {p.elevation ? ` · ${p.elevation} m` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
